import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, type UsageEventGlobal } from '@/lib/api';

type StatusFilter = '' | 'completed' | 'aborted' | 'failed';

export function EventsPage() {
  const [filters, setFilters] = useState<{ user: string; model: string; status: StatusFilter }>({
    user: '',
    model: '',
    status: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['events', filters],
    queryFn: () =>
      adminApi.listEvents({
        user: filters.user.trim() || undefined,
        model: filters.model.trim() || undefined,
        status: (filters.status || undefined) as 'completed' | 'aborted' | 'failed' | undefined,
        limit: 200,
      }),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold tracking-tight">使用事件 (usage_events)</h2>
      <p className="text-sm text-muted-foreground">
        全局账本浏览。 完整字段见 <code className="text-xs bg-muted px-1 rounded">usage_events</code> 表。
        点击行展开 metadata。
      </p>

      <Card>
        <CardContent className="grid gap-3 md:grid-cols-3 pt-6">
          <Field label="用户手机号或 UUID">
            <Input
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value })}
            />
          </Field>
          <Field label="model (精确)">
            <Input
              placeholder="moonshotai/Kimi-K2.5"
              value={filters.model}
              onChange={(e) => setFilters({ ...filters, model: e.target.value })}
            />
          </Field>
          <Field label="status">
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) => setFilters({ ...filters, status: (v === 'all' ? '' : v) as StatusFilter })}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="completed">completed</SelectItem>
                <SelectItem value="aborted">aborted</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
      {data && (
        <>
          <p className="text-xs text-muted-foreground">共 {data.length} 条（最多 200）</p>
          <Table>
            <THead>
              <TR>
                <TH className="w-40">时间</TH>
                <TH>用户</TH>
                <TH>seat / ip</TH>
                <TH>model</TH>
                <TH>in / cached / out</TH>
                <TH>credit</TH>
                <TH>status</TH>
                <TH>metadata</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((r) => (
                <EventRow key={r.id} row={r} />
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
}

function EventRow({ row }: { row: UsageEventGlobal }) {
  const [expanded, setExpanded] = useState(false);
  const cachedRaw = row.metadata?.cached_tokens;
  const cached = typeof cachedRaw === 'number' ? cachedRaw : Number(cachedRaw ?? 0) || 0;
  const statusColor =
    row.status === 'completed'
      ? 'bg-green-100 text-green-800'
      : row.status === 'aborted'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';

  return (
    <TR onClick={() => setExpanded((v) => !v)} className="cursor-pointer">
      <TD className="text-xs whitespace-nowrap">{new Date(row.created_at).toLocaleString('zh-CN')}</TD>
      <TD title={row.user_id}>
        <div className="text-xs">{row.user_phone ? `手机号：${row.user_phone}` : '手机号未设置'}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{row.user_id.slice(0, 8)}…</div>
      </TD>
      <TD className="font-mono text-[10px]">
        {row.seat_id ? row.seat_id.slice(0, 8) : '—'} / {row.ip_id ? row.ip_id.slice(0, 8) : '—'}
      </TD>
      <TD className="font-mono text-xs">{row.model}</TD>
      <TD className="text-xs">
        {row.input_tokens} / {cached} / {row.output_tokens}
      </TD>
      <TD className="font-mono text-xs">{row.credits}</TD>
      <TD>
        <span className={`text-xs px-2 py-0.5 rounded ${statusColor}`}>{row.status}</span>
      </TD>
      <TD className="text-xs">
        {expanded ? (
          <pre className="font-mono text-[10px] bg-muted p-2 rounded max-h-60 overflow-auto whitespace-pre-wrap">
            {JSON.stringify(row.metadata, null, 2)}
          </pre>
        ) : (
          <span className="text-muted-foreground">{row.metadata ? '(点击展开)' : '—'}</span>
        )}
      </TD>
    </TR>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
